import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  useColorMode
} from '@chakra-ui/react';
import { InputBoxSingle } from '@peacecover/component';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import ProfileAvatar from '../../presentation/my-page/components/ProfileAvatar';
import PulsingGradientCircle from '../../presentation/chat/components/PulsingGradientCircle';
import type { components } from '../../types/schema';
import { useUpdateBettieProfile } from '../../domains/bettie/ui/hooks/useUpdateBettieProfile';

interface BettieProfileUpdateBottomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  bettieProfile: components['schemas']['ApiV1BettieProfile'];
  chatRoomId: string;
  currentNickname: string;
}

const BettieNicknameUpdateBottomDrawer = ({
  isOpen,
  onClose,
  bettieProfile,
  chatRoomId,
  currentNickname
}: BettieProfileUpdateBottomDrawerProps) => {
  const { colorMode } = useColorMode();
  const [nickname, setNickname] = useState(currentNickname ?? '');
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: updateBettieProfile } = useUpdateBettieProfile();

  const handleUpdateNickname = async () => {
    setIsLoading(true);
    const responseBody = {
      chat_room: {
        user_nickname: nickname
      }
    };
    try {
      const response = await updateBettieProfile({ body: responseBody, chatRoomId });
      if (response.chatRoom) {
        onClose();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setNickname('');
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setNickname('');
      }}
      placement="bottom"
    >
      <DrawerOverlay />
      <DrawerContent
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
        style={{
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          boxShadow:
            colorMode === 'dark'
              ? '0 -6px 7.5px 0 rgba(0, 0, 0, 0.65)'
              : '0 -5px 8.8px 0 rgba(0, 0, 0, 0.07)'
        }}
      >
        <DrawerBody paddingY="40px" paddingX="24px">
          <div className="flex flex-col items-center gap-[32px]">
            <p className="text-[17px] font-bold leading-[20px] text-pure-black dark:text-pure-white text-center">
              {t('BettieChatRoom.nicknameUpdateDrawer.title')}
            </p>
            <div className="flex justify-center gap-[20px] items-center">
              <PulsingGradientCircle
                gradientCircleSize="50px"
                glassCircleSize="50px"
                showDottedCircle={false}
                blur="4.7px"
              >
                <ProfileAvatar
                  thumbnailUrl={bettieProfile?.thumbnailUrl || ''}
                  widthAndHeight="45px"
                  svgWidthAndHeight="45px"
                />
              </PulsingGradientCircle>
              <HorizontalSpeechBallon
                text={t('BettieChatRoom.nicknameUpdateDrawer.chatBubble', {
                  nickname: nickname + (nickname.length > 0 ? '!' : '')
                })}
              />
            </div>
            <InputBoxSingle
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t('BettieChatRoom.nicknameUpdateDrawer.placeholder') as string}
              maxLength={10}
              showIcon={true}
              onClear={() => setNickname('')}
              className="w-full bg-transparent"
            />
          </div>
        </DrawerBody>
        <DrawerFooter p={0}>
          <button
            type="button"
            className="bg-primary-light dark:bg-primary-dark w-full pt-[14px] pb-[calc(env(safe-area-inset-bottom)+14px)]"
            onClick={handleUpdateNickname}
            disabled={isLoading}
          >
            <p className="text-center text-on-primary text-[17px] font-semibold leading-[20px]">
              {t('done')}
            </p>
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BettieNicknameUpdateBottomDrawer;

export const HorizontalSpeechBallon = ({
  text,
  enableTyping = false,
  onTypingComplete
}: {
  text: string;
  enableTyping?: boolean;
  onTypingComplete?: () => void;
}) => {
  const { colorMode } = useColorMode();
  const bg = colorMode === 'light' ? '#FFFFFF' : '#222222';
  const SPEED = 100;
  const [displayText, setDisplayText] = useState(enableTyping ? '' : text);

  useEffect(() => {
    if (!enableTyping) {
      setDisplayText(text);
      if (typeof onTypingComplete === 'function') {
        onTypingComplete();
      }
      return;
    }
    let i = 0;
    setDisplayText('');
    const textArray = Array.from(text);
    const timer = setInterval(() => {
      setDisplayText(textArray.slice(0, i + 1).join(''));
      i++;
      if (i >= textArray.length) {
        clearInterval(timer);
        if (typeof onTypingComplete === 'function') {
          onTypingComplete();
        }
      }
    }, SPEED);
    return () => clearInterval(timer);
  }, [text, enableTyping, onTypingComplete]);

  return (
    <div
      className="relative inline-block align-top"
      style={{
        maxWidth: 296,
        filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.04)) drop-shadow(0 0 3px rgba(0,0,0,0.07))',
        isolation: 'isolate'
      }}
    >
      {/* 본체 */}
      <div className="relative rounded-[20px]" style={{ background: bg, padding: '18px 20px' }}>
        <p className="text-[16px] font-semibold text-text-main-lv2 leading-[19px] text-center whitespace-pre-wrap break-words">
          {displayText}
          {enableTyping && displayText.length < text.length - 1 && (
            <span className="animate-pulse">|</span>
          )}
        </p>
      </div>

      <svg
        width={18}
        height={20}
        viewBox="0 0 22 28"
        aria-hidden="true"
        preserveAspectRatio="none"
        className="absolute top-1/2 pointer-events-none"
        style={{
          left: 0,
          transform: 'translate(calc(-100% + 9px), -50%)'
        }}
        aria-label="Chat Bubble Tail"
        role="img"
      >
        <path
          d="
            M22,0
            L22,28
            Q0,22 0,14
            Q0,6 22,0
            Z
          "
          fill={bg}
        />
      </svg>
    </div>
  );
};
